/// <amd-module name="Search/Code/Flux/StoresHub" />
import * as CompositeStoresManager from "Search/Shared/Base/Stores/CompositeStoresManager";
import { IStoresHub } from "Search/Shared/Base/Container"
import { Filter } from "SearchUI/Utilities/Filter";
import { IPreviewSetting, ISortField, ISeedData } from "Search/Shared/Base/Types";
import { Action } from "VSS/Reflux/Action";
import { ActionsHub } from "Search/Code/Flux/ActionsHub";
import { ItemContentStore, IItemContentStoreState } from "Search/Code/Flux/Stores/ItemContentStore";
import { PreviewOrientationStore } from "Search/Code/Flux/Stores/PreviewOrientationStore";
import { SearchStore } from "Search/Code/Flux/Stores/SearchStore";
import { ZeroDataStore } from "Search/Code/Flux/Stores/ZeroDataStore";
import { ZeroDataState } from "Search/Shared/Base/Stores/ZeroDataStore";
import { ISearchStoreState } from "Search/Shared/Base/Stores/SearchStore";
import { SortOptionsStore } from "Search/Code/Flux/Stores/SortOptionsStore";
import { FilterStore } from "Search/Code/Flux/Stores/FilterStore";
import { IFilterStoreState } from "Search/Shared/Base/Stores/FilterStore";
import { NotificationStore, INotificationStoreState } from "Search/Code/Flux/Stores/NotificationStore";
import { PreviewStore, IPreviewStoreState } from "Search/Code/Flux/Stores/PreviewStore";
import { IPreviewOrientationStoreState } from "Search/Shared/Base/Stores/PreviewOrientationStore";
import { SearchQuery, CodeQueryResponse, CodeResult } from "Search/LegacyWebApi/Contracts/Code";
import { EntitySortOption, EntitySearchResponseWithActivityId } from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import { IProjectData } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { SearchSourceType } from "Search/Shared/Base/Sources/SearchSource";
import { CodeSearchPageDataProviderContributionId } from "Search/Code/Constants";

export type StoreName =
    "searchStore" |
    "itemContentStore" |
    "previewOrientationStore" |
    "zeroDataStore" |
    "sortOptionsStore" |
    "notificationStore" |
    "previewStore" |
    "filterStore";

export interface IAggregatedState {
    isProjectContext: boolean;

    availableSortFields: ISortField[];

    appliedEntitySortOption: EntitySortOption;

    availablePreviewOrientations: IPreviewSetting[];

    previewOrientationStoreState: IPreviewOrientationStoreState;

    searchStoreState: ISearchStoreState<SearchQuery, CodeQueryResponse>;

    zeroDataState: ZeroDataState;

    notificationStoreState: INotificationStoreState;

    itemContentStoreState: IItemContentStoreState;

    previewStoreState: IPreviewStoreState;

    filterStoreState: IFilterStoreState;
}

export class StoresHub implements IStoresHub<StoreName, IAggregatedState>{
    private readonly compositeStoresManager = new CompositeStoresManager.CompositeStoresManager();
    private readonly listener: CompositeStoresManager.ListeningActionsManager;

    public searchStore: SearchStore;
    public itemContentStore: ItemContentStore;
    public previewOrientationStore: PreviewOrientationStore;
    public zeroDataStore: ZeroDataStore;
    public sortOptionStore: SortOptionsStore;
    public notificationStore: NotificationStore;
    public previewStore: PreviewStore;
    public filterStore: FilterStore;

    constructor(
        private readonly actionsHub: ActionsHub,
        private readonly project: IProjectData | undefined,
        filter: Filter,
        isFullScreen: boolean,
        private readonly pageContext: IVssPageContext,
        private readonly serverSortEnabled: boolean,
        private readonly includeFacets: boolean,
        private readonly isTabSwitch: boolean,
        private readonly onDispatched?: CompositeStoresManager.EmitChangedFunction<any>
    ) {
        this.listener = new CompositeStoresManager.ListeningActionsManager(this.emitChanged);
        this.searchStore = this.createSearchStore(this.actionsHub);
        this.itemContentStore = this.createItemContentStore(this.actionsHub);
        this.previewOrientationStore = this.createPreviewOrientationStore(this.actionsHub);
        this.zeroDataStore = this.createZeroDataStore(actionsHub);
        this.sortOptionStore = this.createSortOptionsStore(actionsHub);
        this.notificationStore = this.createNotificationStore(actionsHub);
        this.previewStore = this.createPreviewStore(actionsHub, isFullScreen);
        this.filterStore = this.createFilterStore(actionsHub, filter);
    }

    public getAggregatedState = (): IAggregatedState => {
        return {
            isProjectContext: !!this.project,
            searchStoreState: this.searchStore.state,
            itemContentStoreState: this.itemContentStore.state,
            previewOrientationStoreState: this.previewOrientationStore.state,
            zeroDataState: this.zeroDataStore.state,
            availableSortFields: this.sortOptionStore.availableSortFields,
            appliedEntitySortOption: this.sortOptionStore.state.sortOption,
            availablePreviewOrientations: this.previewOrientationStore.availablePreviewOrientations,
            notificationStoreState: this.notificationStore.state,
            previewStoreState: this.previewStore.state,
            filterStoreState: this.filterStore.state
        };
    };

    public getCompositeStore = (storeNames: StoreName[]): CompositeStoresManager.CompositeStore => {
        return this.compositeStoresManager.getOrCreate(storeNames);
    }

    public dispose = (): void => {
        this.listener.dispose();
        this.compositeStoresManager.dispose();
    };

    private get seedData(): ISeedData<CodeResult> | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const responseWithActivityId = contributionService.getData<EntitySearchResponseWithActivityId | undefined>(CodeSearchPageDataProviderContributionId);
        if (!this.isTabSwitch && responseWithActivityId && responseWithActivityId.response) {
            const response = responseWithActivityId.response as CodeQueryResponse;
            const items = response && response.results && response.results.values;
            const activeItem = items && items.length > 0 ? items[0] : undefined;
            return {
                responseWithActivityId,
                source: SearchSourceType.DataProvider,
                activeItem
            };
        }
    }

    private createSearchStore(actionsHub: ActionsHub): SearchStore {
        const store = new SearchStore("SearchStoreChanged", this.seedData);

        this.listener.listen(actionsHub.pageInitializationStarted, "searchStore", (payload) => {
            payload.isLandingPage ? store.updateQuery(payload) : store.startSearch(payload);
        });
        this.listener.listen(actionsHub.resultsLoaded, "searchStore", store.loadSearchResults);
        this.listener.listen(actionsHub.searchStarted, "searchStore", store.startSearch);
        this.listener.listen(actionsHub.sortOptionChanged, "searchStore", store.updateItemsOnSort);
        this.listener.listen(actionsHub.searchFailed, "searchStore", store.failSearch);

        this.listener.listen(actionsHub.DataRefreshInfoLoaded, "searchStore", store.updateRefreshInfo);
       
        return store;
    }

    private createItemContentStore(actionsHub: ActionsHub): ItemContentStore {
        const store = new ItemContentStore("ItemContentStoreChanged", this.seedData);

        this.listener.listen(actionsHub.itemChanged, "itemContentStore", store.changeActiveItem);
        this.listener.listen(actionsHub.resultsLoaded, "itemContentStore", payload => store.changeActiveItem({ item: payload.activeItem }));
        this.listener.listen(actionsHub.sortOptionChanged, "itemContentStore", payload => store.changeActiveItem({ item: payload.sortedItems[0] }));

        return store;
    }

    private createPreviewOrientationStore(actionsHub: ActionsHub): PreviewOrientationStore {
        const store = new PreviewOrientationStore("PreviewOrientationStoreChanged", this.pageContext);

        this.listener.listen(actionsHub.previewOrientationChanged, "previewOrientationStore", store.updatePreviewOrientationMode);

        return store;
    }

    private createZeroDataStore(actionsHub: ActionsHub): ZeroDataStore {
        const store = new ZeroDataStore("ZeroDataStoreChanged", this.pageContext, this.seedData);

        this.listener.listen(actionsHub.pageInitializationStarted, "zeroDataStore", (payload) => {
            if (payload.isLandingPage) {
                store.onLandingPage();
            }
        });
        this.listener.listen(actionsHub.resultsLoaded, "zeroDataStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            const { query } = searchStoreState;
            const fetchMoreScenario = searchStoreState.fetchMoreScenario || false;
            store.onResultsLoaded(query, payload, fetchMoreScenario);
        });
        this.listener.listen(actionsHub.searchFailed, "zeroDataStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            store.onSearchFailed(payload, searchStoreState.query);
        });
        this.listener.listen(actionsHub.searchStarted, "zeroDataStore", store.reset);

        return store;
    }

    private createSortOptionsStore(actionsHub: ActionsHub): SortOptionsStore {
        const store = new SortOptionsStore("SortOptionsStoreChanged", this.pageContext, this.serverSortEnabled);

        this.listener.listen(actionsHub.sortOptionChanged, "sortOptionsStore", store.changeSortOption);
        if (this.serverSortEnabled) {
            this.listener.listen(actionsHub.pageInitializationStarted, "sortOptionsStore", (payload) => {
                !payload.isLandingPage && store.updateSortOptionOnSearch(payload);
            });
        }
        this.listener.listen(actionsHub.searchStarted, "sortOptionsStore", store.updateSortOptionOnSearch);

        return store;
    }

    private createNotificationStore(actionsHub: ActionsHub): NotificationStore {
        const store = new NotificationStore("NotificationStoreChanged", this.seedData);
        this.listener.listen(actionsHub.resultsLoaded, "notificationStore", store.onSearchResultsLoaded);
        this.listener.listen(actionsHub.StaleBranchBannerLoaded, "notificationStore", store.onStaleBranchLoaded);
        return store;
    }

    private createPreviewStore(actionsHub: ActionsHub, isFullScreen: boolean): PreviewStore {
        const store = new PreviewStore("PreviewStoreChanged", isFullScreen);

        this.listener.listen(actionsHub.activeTabChanged, "previewStore", store.changeActiveTab);
        this.listener.listen(actionsHub.fullScreenToggled, "previewStore", store.toggleFullScreen);
        this.listener.listen(actionsHub.pageInitializationStarted, "previewStore", ({ activeTabKey }) => store.initActiveTab(activeTabKey));

        return store;
    }

    private createFilterStore(actionsHub: ActionsHub, filter: Filter): FilterStore {
        const store = new FilterStore("FilterStoreChanged", filter, this.pageContext, this.project, this.includeFacets, this.seedData);

        this.listener.listen(actionsHub.facetsLoaded, "filterStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            store.updateFilters(searchStoreState.query, true, payload.response);
        });

        // listen on resultsLoaded only when includeFacets is true otherwise during filter application
        // scenario, filter component would be notified on resultsLoaded also resulting in undesired state.
        if (this.includeFacets) {
            this.listener.listen(actionsHub.resultsLoaded, "filterStore", (payload) => {
                const { searchStoreState } = this.getAggregatedState();
                store.updateFilters(searchStoreState.query, true, payload.response);
            });
        }

        this.listener.listen(actionsHub.fetchfacetsFailed, "filterStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            store.failFacetsLoad(searchStoreState.query, payload);
        });

        this.listener.listen(actionsHub.filterPaneToggled, "filterStore", store.toggleFilterPane);

        // listen only when facets are fetched on demand        
        if(!this.includeFacets) {
            this.listener.listen(actionsHub.facetsUpdated, "filterStore", (payload) => {
                const { searchStoreState } = this.getAggregatedState();
                store.updateFilters(searchStoreState.query, false, payload.response);
            });

            // incase of advance Query when SeedData isn't available on PageInitialization
            this.listener.listen(actionsHub.pageInitializationStarted, "filterStore", () => {
                const { searchStoreState } = this.getAggregatedState();
                store.updateFilters(searchStoreState.query, false, undefined);
            });
        }

        return store;
    }

    private emitChanged = (changedStores: string[], action: Action<any>, payload: any): void => {
        this.compositeStoresManager.emitCompositeChanged(changedStores);

        if (this.onDispatched) {
            this.onDispatched(changedStores, action, payload);
        }
    };
}
